import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  createFilterOptions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { collection, onSnapshot, orderBy, query, DocumentData } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';

export interface CourseOption {
  id: string;
  name: string;
  code?: string;
}

interface TermRow {
  id: string;
  name: string;
  status?: string;
  startDate?: string;
}

const ALL_PERIODS = '__all__';

// Autocomplete: match on both the course code and name.
const courseFilter = createFilterOptions<CourseOption>({
  stringify: (c) => `${c.code ?? ''} ${c.name}`,
});

interface CoursePickerProps {
  /** Selected catalog course id (controlled). '' = none linked. */
  value: string;
  /** Called with the new course id ('' when cleared). */
  onChange: (courseId: string) => void;
  /** Full catalog of courses the parent already holds. */
  courses: CourseOption[];
  /** Label for the course autocomplete (defaults to the template-editor course label). */
  label?: string;
}

/**
 * Scalable catalog-course selector: a Period filter (defaults to the active
 * term) that scopes the course list to those offered that period, plus a
 * searchable course autocomplete. A "Todos los períodos" option always exposes
 * the full catalog, and the currently-linked course is never hidden even if it
 * has no section in the selected period (templates are often authored before
 * any section exists). Owns its own term/section loading; the parent only holds
 * the selected course id + the course catalog.
 */
export function CoursePicker({ value, onChange, courses, label }: CoursePickerProps) {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();

  const [terms, setTerms] = useState<TermRow[]>([]);
  // Map termId -> set of courseIds offered (has a non-cancelled section) that period.
  const [courseIdsByTerm, setCourseIdsByTerm] = useState<Record<string, Set<string>>>({});
  const [selectedTermId, setSelectedTermId] = useState<string>('');

  // Load terms (period filter + active-term default), newest first.
  useEffect(() => {
    if (!currentTenantId) return;
    const unsub = onSnapshot(
      query(collection(db, `tenants/${currentTenantId}/terms`), orderBy('startDate', 'desc')),
      (snap) => {
        setTerms(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<TermRow, 'id'>) })) as TermRow[],
        );
      },
    );
    return () => unsub();
  }, [currentTenantId]);

  // Load sections → which courses are offered per term.
  useEffect(() => {
    if (!currentTenantId) return;
    const unsub = onSnapshot(collection(db, `tenants/${currentTenantId}/sections`), (snap) => {
      const map: Record<string, Set<string>> = {};
      snap.docs
        .map((d) => d.data() as DocumentData)
        .filter((s) => s['status'] !== 'cancelled')
        .forEach((s) => {
          const termId = s['termId'] as string;
          const courseId = s['courseId'] as string;
          if (!termId || !courseId) return;
          (map[termId] ??= new Set<string>()).add(courseId);
        });
      setCourseIdsByTerm(map);
    });
    return () => unsub();
  }, [currentTenantId]);

  // Only terms that actually offer courses (don't clutter the period dropdown).
  const termsWithCourses = useMemo(
    () => terms.filter((tm) => (courseIdsByTerm[tm.id]?.size ?? 0) > 0),
    [terms, courseIdsByTerm],
  );

  // Default the period to the active term (in progress → open → newest) once ready.
  useEffect(() => {
    if (selectedTermId || termsWithCourses.length === 0) return;
    const active =
      termsWithCourses.find((tm) => tm.status === 'in_progress') ??
      termsWithCourses.find((tm) => tm.status === 'open') ??
      termsWithCourses[0];
    if (active) setSelectedTermId(active.id);
  }, [termsWithCourses, selectedTermId]);

  // Courses visible in the autocomplete: full catalog for "all periods", else
  // those offered in the selected period — but always keep the current value.
  const visibleCourses = useMemo(() => {
    if (selectedTermId === ALL_PERIODS || !selectedTermId) return courses;
    const offered = courseIdsByTerm[selectedTermId] ?? new Set<string>();
    return courses.filter((c) => offered.has(c.id) || c.id === value);
  }, [courses, courseIdsByTerm, selectedTermId, value]);

  const selectedCourse = useMemo(
    () => courses.find((c) => c.id === value) ?? null,
    [courses, value],
  );

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
      {/* Period filter — defaults to the active term, scopes the course list */}
      <FormControl size="small" sx={{ minWidth: 190 }}>
        <InputLabel>{t('templateEditor.identity.period')}</InputLabel>
        <Select
          value={
            selectedTermId === ALL_PERIODS ||
            termsWithCourses.some((tm) => tm.id === selectedTermId)
              ? selectedTermId
              : ''
          }
          label={t('templateEditor.identity.period')}
          onChange={(e) => setSelectedTermId(e.target.value)}
        >
          <MenuItem value={ALL_PERIODS}>{t('templateEditor.identity.allPeriods')}</MenuItem>
          {termsWithCourses.map((tm) => (
            <MenuItem key={tm.id} value={tm.id}>
              {tm.name}
              {tm.status === 'in_progress' ? ` · ${t('lms.section.activeTag')}` : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Searchable course picker — scoped to the selected period (or all) */}
      <Autocomplete<CourseOption>
        size="small"
        sx={{ minWidth: 260, flex: 1 }}
        options={visibleCourses}
        value={selectedCourse}
        onChange={(_, val) => onChange(val?.id ?? '')}
        getOptionLabel={(c) => (c.code ? `${c.code} — ${c.name}` : c.name)}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        filterOptions={courseFilter}
        noOptionsText={t('templateEditor.identity.courseNone')}
        renderInput={(params) => (
          <TextField
            // MUI render params spread trips exactOptionalPropertyTypes; cast is the standard escape.
            {...(params as unknown as React.ComponentProps<typeof TextField>)}
            size="small"
            label={label ?? t('templateEditor.identity.course')}
          />
        )}
        renderOption={({ key: _key, ...optionProps }, c) => (
          <Box component="li" key={c.id} {...optionProps}>
            <Typography variant="body2" component="span">
              {c.code ? `${c.code} — ` : ''}
              {c.name}
            </Typography>
          </Box>
        )}
      />
    </Stack>
  );
}
